/**
 * The WebGL2 glyph renderer.
 *
 * One instanced quad, one draw call for the terrain and one for the sprites.
 * Per-instance attributes carry the cell position, the atlas index and the two
 * colours; the fragment shader samples the atlas's alpha as a stencil and
 * mixes background into foreground. Nothing is uploaded per glyph, no geometry
 * is rebuilt, and the whole grid costs two `drawArraysInstanced` calls.
 *
 * That is what buys 60 fps with a full screen of animated glyphs on integrated
 * graphics (M0-05). The naive version — a textured quad drawn per cell — is
 * three thousand draw calls a frame, which is fine on a desktop GPU and
 * miserable on the laptops people will actually play this on.
 *
 * A **tint** slot is reserved in the shader now, per M0-05's brief: a per-cell
 * multiply that M3-05's lighting and weather will drive. It costs one
 * multiply by white today and saves rewriting the pipeline later.
 */

import type { GlyphAtlas, Renderer, Sprite, TerrainFrame } from './renderer.js';

const VERTEX_SHADER = `#version 300 es
precision highp float;

// Unit quad, expanded per instance.
layout(location = 0) in vec2 a_corner;

layout(location = 1) in vec2 a_position;   // pixel position of the cell
layout(location = 2) in float a_glyph;     // atlas index
layout(location = 3) in vec3 a_fg;
layout(location = 4) in vec4 a_bg;

uniform vec2 u_resolution;
uniform vec2 u_cell;
uniform vec2 u_atlasCells;

out vec2 v_uv;
out vec3 v_fg;
out vec4 v_bg;

void main() {
  vec2 pixel = a_position + a_corner * u_cell;

  // Pixel space (y down) to clip space (y up).
  vec2 clip = (pixel / u_resolution) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);

  float column = mod(a_glyph, u_atlasCells.x);
  float row = floor(a_glyph / u_atlasCells.x);
  v_uv = (vec2(column, row) + a_corner) / u_atlasCells;

  v_fg = a_fg;
  v_bg = a_bg;
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_uv;
in vec3 v_fg;
in vec4 v_bg;

uniform sampler2D u_atlas;

// Reserved for M3-05's lighting and weather: a per-frame multiply today,
// a per-cell one when there is something to drive it.
uniform vec3 u_tint;

out vec4 outColour;

void main() {
  // The atlas is a 1-bit stencil in the alpha channel.
  float ink = texture(u_atlas, v_uv).a;

  vec3 colour = mix(v_bg.rgb, v_fg, ink);
  float alpha = mix(v_bg.a, 1.0, ink);

  outColour = vec4(colour * u_tint * alpha, alpha);
}
`;

/** Per-instance floats: x, y, glyph, r, g, b, bgR, bgG, bgB, bgA. */
const STRIDE = 10;

function unpack(colour: number): [number, number, number] {
  return [((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255];
}

export class WebGLRenderer implements Renderer {
  readonly kind = 'webgl2' as const;

  private readonly gl: WebGL2RenderingContext;
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly texture: WebGLTexture;
  private readonly uniforms: Record<string, WebGLUniformLocation | null>;
  private readonly atlas: GlyphAtlas;

  private instances = new Float32Array(STRIDE * 4096);

  constructor(canvas: HTMLCanvasElement, atlas: GlyphAtlas) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('WebGL2 is not available');

    this.gl = gl;
    this.atlas = atlas;
    this.program = link(gl, VERTEX_SHADER, FRAGMENT_SHADER);

    this.uniforms = {
      resolution: gl.getUniformLocation(this.program, 'u_resolution'),
      cell: gl.getUniformLocation(this.program, 'u_cell'),
      atlasCells: gl.getUniformLocation(this.program, 'u_atlasCells'),
      atlas: gl.getUniformLocation(this.program, 'u_atlas'),
      tint: gl.getUniformLocation(this.program, 'u_tint'),
    };

    const vao = gl.createVertexArray();
    if (!vao) throw new Error('could not create a vertex array');
    this.vao = vao;
    gl.bindVertexArray(vao);

    // The unit quad, as two triangles. Never changes.
    const corners = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, corners);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const instanceBuffer = gl.createBuffer();
    if (!instanceBuffer) throw new Error('could not create an instance buffer');
    this.instanceBuffer = instanceBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);

    const bytes = STRIDE * 4;
    const attribute = (location: number, size: number, offset: number) => {
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, size, gl.FLOAT, false, bytes, offset * 4);
      gl.vertexAttribDivisor(location, 1);
    };
    attribute(1, 2, 0); // position
    attribute(2, 1, 2); // glyph
    attribute(3, 3, 3); // fg
    attribute(4, 4, 6); // bg

    this.texture = uploadAtlas(gl, atlas);

    gl.bindVertexArray(null);
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  resize(width: number, height: number): void {
    this.gl.viewport(0, 0, width, height);
  }

  draw(terrain: TerrainFrame, sprites: readonly Sprite[]): void {
    const gl = this.gl;
    const { cellWidth, cellHeight } = this.atlas;

    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);

    gl.uniform2f(this.uniforms['resolution']!, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.uniform2f(this.uniforms['cell']!, cellWidth, cellHeight);
    gl.uniform2f(this.uniforms['atlasCells']!, this.atlas.columns, this.atlas.rows);
    gl.uniform3f(this.uniforms['tint']!, 1, 1, 1);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(this.uniforms['atlas']!, 0);

    const count = this.fill(terrain, sprites, cellWidth, cellHeight);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.instances.subarray(0, count * STRIDE), gl.DYNAMIC_DRAW);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, count);

    gl.bindVertexArray(null);
  }

  /** Pack every cell and sprite into the instance array. Returns the count. */
  private fill(
    terrain: TerrainFrame,
    sprites: readonly Sprite[],
    cellWidth: number,
    cellHeight: number,
  ): number {
    const total = terrain.columns * terrain.rows + sprites.length;
    if (this.instances.length < total * STRIDE) {
      this.instances = new Float32Array(total * STRIDE * 2);
    }

    const data = this.instances;
    let at = 0;

    for (let row = 0; row < terrain.rows; row += 1) {
      for (let column = 0; column < terrain.columns; column += 1) {
        const index = row * terrain.columns + column;
        const [r, g, b] = unpack(terrain.fg[index] ?? 0);
        const [br, bg, bb] = unpack(terrain.bg[index] ?? 0);

        data[at] = terrain.offsetX + column * cellWidth;
        data[at + 1] = terrain.offsetY + row * cellHeight;
        data[at + 2] = terrain.glyphs[index] ?? 0;
        data[at + 3] = r;
        data[at + 4] = g;
        data[at + 5] = b;
        data[at + 6] = br;
        data[at + 7] = bg;
        data[at + 8] = bb;
        data[at + 9] = 1;
        at += STRIDE;
      }
    }

    for (const sprite of sprites) {
      const [r, g, b] = unpack(sprite.fg);

      data[at] = sprite.x;
      data[at + 1] = sprite.y;
      data[at + 2] = sprite.glyph;
      data[at + 3] = r;
      data[at + 4] = g;
      data[at + 5] = b;
      // Transparent background, so the terrain shows through around the glyph.
      data[at + 6] = 0;
      data[at + 7] = 0;
      data[at + 8] = 0;
      data[at + 9] = 0;
      at += STRIDE;
    }

    return total;
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteTexture(this.texture);
    gl.deleteBuffer(this.instanceBuffer);
    gl.deleteVertexArray(this.vao);
    gl.deleteProgram(this.program);
  }
}

function uploadAtlas(gl: WebGL2RenderingContext, atlas: GlyphAtlas): WebGLTexture {
  const texture = gl.createTexture();
  if (!texture) throw new Error('could not create the atlas texture');

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, atlas.image);

  // NEAREST, and no mipmaps. A bitmap font filtered bilinearly is a blurry
  // bitmap font, which is the one thing this whole aesthetic cannot survive.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  return texture;
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('could not create a shader');

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`glyph shader failed to compile: ${log ?? 'no log'}`);
  }
  return shader;
}

function link(gl: WebGL2RenderingContext, vertex: string, fragment: string): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error('could not create a program');

  const vs = compile(gl, gl.VERTEX_SHADER, vertex);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fragment);

  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`glyph program failed to link: ${log ?? 'no log'}`);
  }
  return program;
}
